import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useMutation } from '@tanstack/react-query';
import { signupApi } from '@/services/api';
import { EMAIL_VERIFICATION_POLL } from '@/config/constants';
import { debug } from '@/utils/debug';
import { LoadingSpinner } from '@/components/ui';

interface EmailVerificationPendingModalProps {
  isOpen: boolean;
  onVerified: () => void;
  email: string;
  memberUuid: string;
}

export function EmailVerificationPendingModal({
  isOpen,
  onVerified,
  email,
  memberUuid,
}: EmailVerificationPendingModalProps) {
  // Ref so the polling effect doesn't tear down every time the parent re-renders
  // and passes a fresh `onVerified` lambda. The effect must stay alive across
  // those renders or the setTimeout chain gets cleared and restarted on every
  // render and polling either storms the API or stops early.
  const onVerifiedRef = useRef(onVerified);
  useEffect(() => {
    onVerifiedRef.current = onVerified;
  }, [onVerified]);

  const [pollingExpired, setPollingExpired] = useState(false);
  // Bumped on a successful resend to restart polling with a fresh deadline.
  const [pollingGeneration, setPollingGeneration] = useState(0);

  // Resend verification email mutation
  const resendMutation = useMutation({
    mutationFn: () => signupApi.resendVerificationEmail({ member_uuid: memberUuid }),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Verification email sent! Please check your inbox.');
        setPollingExpired(false);
        setPollingGeneration((g) => g + 1);
      } else {
        toast.error(response.message || 'Failed to resend email');
      }
    },
    onError: () => {
      toast.error('Failed to resend verification email. Please try again.');
    },
  });

  // Poll for verification status — driven by isOpen + memberUuid + generation.
  useEffect(() => {
    if (!isOpen || !memberUuid) return;

    // Modal stays mounted (returns null when closed), so React state survives
    // close/reopen — reset the expired flag for the new polling session.
    setPollingExpired(false);

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const startTime = Date.now();

    const check = async () => {
      if (cancelled) return;
      try {
        const status = await signupApi.checkEmailVerificationStatus(memberUuid);
        if (cancelled) return;
        debug.log('Email verification status:', status);
        if (status.email_verified) {
          toast.success('Email verified successfully!');
          onVerifiedRef.current();
          return;
        }
      } catch (error) {
        debug.error('Error checking verification status:', error);
      }
      if (cancelled) return;
      if (Date.now() - startTime > EMAIL_VERIFICATION_POLL.DEADLINE_MS) {
        setPollingExpired(true);
        return;
      }
      timeoutId = setTimeout(check, EMAIL_VERIFICATION_POLL.INTERVAL_MS);
    };

    check();

    return () => {
      cancelled = true;
      if (timeoutId !== null) clearTimeout(timeoutId);
    };
  }, [isOpen, memberUuid, pollingGeneration]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
        {/* Content */}
        <div className="text-center mb-6">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-4">
            <svg
              className="h-8 w-8 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Verify Your Email</h3>
          <p className="text-gray-600 mb-4">
            We've sent a verification email to:
          </p>
          <p className="text-lg font-semibold text-blue-600 mb-4">{email}</p>
          <p className="text-gray-600 text-sm">
            Please click the "Finish account creation" button in the email to verify your account and continue.
          </p>
        </div>

        {/* Waiting indicator */}
        {pollingExpired ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <p className="text-amber-800 font-medium text-center">
              We haven't detected verification yet.
            </p>
            <p className="text-xs text-amber-700 text-center mt-2">
              Click "Resend Verification Email" below to get a new link, or check your spam folder.
            </p>
          </div>
        ) : (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-center">
              <LoadingSpinner size="md" variant="blue" className="mr-3" label="Waiting for verification" />
              <span className="text-blue-700 font-medium">Waiting for verification...</span>
            </div>
            <p className="text-xs text-blue-600 text-center mt-2">
              This page will automatically update when you verify your email.
            </p>
          </div>
        )}

        {/* Help text */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <h4 className="font-semibold text-gray-900 mb-2">Didn't receive the email?</h4>
          <ul className="text-sm text-gray-600 space-y-1 ml-5 list-disc">
            <li>Check your spam or junk folder</li>
            <li>Make sure you entered the correct email address</li>
            <li>Wait a few minutes for the email to arrive</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3">
          <button
            onClick={() => resendMutation.mutate()}
            disabled={resendMutation.isPending}
            className="w-full px-6 py-3 bg-blue-800 text-white rounded-md hover:bg-blue-900 font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {resendMutation.isPending ? 'Sending...' : 'Resend Verification Email'}
          </button>
        </div>
      </div>
    </div>
  );
}
